import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MAINTENANCE_FLAGS_ROOT } from '../hooks/useMaintenanceFlags';
import { Camera, CheckCircle, AlertTriangle, XCircle, Wrench, Loader2, Plus, X, AlertOctagon } from 'lucide-react';
import DatePicker from '../components/DatePicker';
import SignatureCapture from '../components/shared/SignatureCapture';
import PhotoUploader from '../components/PhotoUploader';
import { api } from '../api/client';
import { calendarDayString, todayCalendarDate, zonedParts } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import {
  type InspectionRow,
  resumeDeficiencies,
  resumeElevatorInfo,
  resumeGeneralInfo,
  resumeItemResults,
  resumeRotarySlipsInfo,
  resumeSlipElevatorInfo,
  resumeTongTruckItems,
  resumeTongTruckNotes,
  resumeTongTruckOverall,
} from '../lib/inspection-resume';
import { inspectionsListPath } from '../lib/inspection-surface';
import { useAuth } from '../contexts/AuthContext';
import {
  INSPECTION_TYPES,
  INSPECTION_TYPE_LABELS,
  inspectionTypeForEquipmentType,
} from '../lib/equipmentInspectionType';
import { toast } from 'sonner';
import { inspectionDisagreementNotice } from '../lib/inspection-result-warning';
import { useHideAgentFab } from '../components/AgentFab';

/**
 * The TENANT's wall-clock time now, 'HH:MM' — the inspection-time seed. It used
 * to read the device clock (`toTimeString`), so a tablet left on another zone
 * pre-filled another zone's hour. '' when the zone is unknown: an input seed,
 * never a rendered value. Local to this file; no shared helper returns HH:MM.
 */
function tenantClockHHMM(timeZone: string | null): string {
  const p = zonedParts(new Date(), timeZone);
  if (!p) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

// ─── Inspection checklists by type ──────────────────────────────────────────

const mkItem = (id: number, name: string) => ({ id, name, status: '', notes: '' });

const TONG_TRUCK_ITEMS = () => [
  // Truck
  mkItem(1, 'Engine oil level & condition'),
  mkItem(2, 'Coolant level & condition'),
  mkItem(3, 'DEF (Diesel Exhaust Fluid) level'),
  mkItem(4, 'Fuel level adequate for shift'),
  mkItem(5, 'Power steering fluid'),
  mkItem(6, 'Brake fluid level'),
  mkItem(7, 'Battery terminals – clean & secure'),
  mkItem(8, 'Belts & hoses – no cracks or leaks'),
  mkItem(9, 'Exhaust system – no leaks or damage'),
  mkItem(10, 'Headlights – high & low beam'),
  mkItem(11, 'Taillights & brake lights'),
  mkItem(12, 'Clearance & marker lights'),
  mkItem(13, 'Reverse alarm functional'),
  mkItem(14, 'Amber strobe / warning lights'),
  mkItem(15, 'Turn signals – front & rear'),
  mkItem(16, 'Four-way hazard lights'),
  mkItem(17, 'Tires – front (tread, pressure, condition)'),
  mkItem(18, 'Tires – rear outer duals'),
  mkItem(19, 'Tires – rear inner duals'),
  mkItem(20, 'Wheel nuts / lug torque indicators'),
  mkItem(21, 'Service brakes – pedal feel & response'),
  mkItem(22, 'Parking / emergency brake'),
  mkItem(23, 'Windshield – no cracks obscuring vision'),
  mkItem(24, 'Wipers & washers functional'),
  mkItem(25, 'Mirrors – adjusted & undamaged'),
  mkItem(26, 'Horn functional'),
  mkItem(27, 'Seatbelts – driver & passenger'),
  mkItem(28, 'Fire extinguisher – charged & accessible'),
  mkItem(29, 'First aid kit – complete & in date'),
  mkItem(30, 'Spill kit present'),
  mkItem(31, 'H₂S personal monitor present & calibrated'),
  mkItem(32, 'Vehicle registration & insurance in cab'),
  // Deck & Load Securement
  mkItem(33, 'Deck boards – no rot, cracks, or missing'),
  mkItem(34, 'Headache rack – secure & undamaged'),
  mkItem(35, 'Tie-down anchor rings – all present'),
  mkItem(36, 'Chains – Grade 70/80, no damage'),
  mkItem(37, 'Chain hooks – no cracks or deformation'),
  mkItem(38, 'All chains fully tensioned – no slack'),
  mkItem(39, 'Binders – no cracks, handles secured'),
  mkItem(40, 'Equipment stable – no shifting possible'),
  mkItem(41, 'Edge protectors in place'),
  mkItem(42, 'Chain & binder count meets NSC Standard 10'),
  mkItem(43, 'No equipment exceeds legal height limits'),
  // Safety
  mkItem(44, 'PPE on board – hard hat, glasses, gloves, steel toes'),
  mkItem(45, 'No hydraulic or fuel leaks visible'),
  mkItem(46, 'Drip pan / absorbent available'),
  mkItem(47, 'Cab clean – no unsecured items'),
];

// Tong Truck is the only type without a server-side template. It keeps the local
// checklist above, but saves through the same casing endpoint as the structured
// types — the submission lands in equipment_inspection_submissions with a NULL
// template_id.
const TONG_TRUCK_TYPE = 'Tong Truck';
const TONG_TRUCK_FORM_TYPE = 'tong_truck';

// Types that use the structured template form instead of the local checklist
const STRUCTURED_TYPES = ['Power Tong', 'CRTi', 'Elevators', 'Slip Elevator L1', 'Rotary Slips L1'] as const;
type StructuredType = typeof STRUCTURED_TYPES[number];

const FORM_TYPE_MAP: Record<StructuredType, string> = {
  'Power Tong': 'power_tong',
  'CRTi': 'crti',
  'Elevators': 'elevators',
  'Slip Elevator L1': 'slip_elevator_l1',
  'Rotary Slips L1': 'rotary_slips_l1',
};

function isStructuredType(type: string): type is StructuredType {
  return (STRUCTURED_TYPES as readonly string[]).includes(type);
}

/**
 * `form_type` back to the label the Type dropdown holds — the inverse of
 * FORM_TYPE_MAP, derived from it rather than written out a second time so the
 * two cannot disagree about a sixth form type.
 *
 * An unrecognised value returns null and the caller leaves the dropdown alone,
 * rather than locking a resumed draft to a guess.
 */
const TYPE_FOR_FORM_TYPE: Record<string, string> = {
  ...Object.fromEntries(Object.entries(FORM_TYPE_MAP).map(([label, key]) => [key, label])),
  [TONG_TRUCK_FORM_TYPE]: TONG_TRUCK_TYPE,
};

function typeForFormType(formType: unknown): string | null {
  if (typeof formType !== 'string') return null;
  return TYPE_FOR_FORM_TYPE[formType] ?? null;
}

// ─── Arriving from an equipment page ────────────────────────────────────────

/**
 * Router state the equipment pages hand this form when a tech taps "Inspect".
 * Every field is optional: the form is also reachable from the Safety quick
 * actions with no state at all, and that path must keep working exactly as it
 * did — manual equipment dropdown, manual type dropdown.
 */
export interface InspectionPrefillState {
  equipmentId?: string;
  /** The tenant's own equipment type name, e.g. "Power Tongs". Free text. */
  equipmentType?: string;
  equipmentData?: Record<string, any>;
  /**
   * Set when the tech arrived from a job's equipment card rather than from the
   * asset's own page. `jobLocation` is deliberately its own key rather than
   * `equipmentData.location`: the two mean different things, and the equipment
   * list query wins the merge in `selectedEquipment` below — a job location
   * folded into the equipment record would be overwritten by the asset's yard
   * as soon as that query resolved.
   */
  jobId?: string;
  jobName?: string;
  /** Where the job is — rig, well or pad. Not the asset's yard. */
  jobLocation?: string;
}

/** "TT-04 - Tong Truck" — the same label the equipment dropdown renders. */
function equipmentLabel(eq: any): string {
  if (!eq) return '';
  const asset = eq.asset_number || eq.assetNumber || eq.unit_number || eq.unitNumber;
  const name = eq.name || eq.equipment_type || eq.type || 'Equipment';
  return asset ? `${asset} - ${name}` : name;
}

// ─── Types for structured template ─────────────────────────────────────────

interface TemplateItem {
  id: string;
  label: string;
}

interface TemplateSection {
  id: string;
  number?: number | string;
  title: string;
  items: (TemplateItem | string)[];
}

interface InspectionTemplate {
  form_type: string;
  title: string;
  sections: TemplateSection[];
}

/**
 * A signature on an inspection, uploaded to R2 and stored as a key.
 *
 * WHY THE GATE. `SignatureCapture` files the image against a real
 * `uploads` row keyed (entity_type, entity_id), so the draft submission has to
 * exist before the pad is interactive — the same rule PhotoUploader follows
 * above, and the CRT inspection bug is what happens when an uploader goes live
 * before the draft POST resolves.
 *
 * WHY THERE IS NO FALLBACK ARM. PhotoUploader degrades to `equipment` when no
 * draft exists, because a photo filed against the asset is still a photo of the
 * asset. A signature has nowhere else to go: filed against the equipment it
 * would attach to the tool rather than to the record somebody signed, and
 * `inspector_signature` on this submission would stay null either way. So the
 * absence is stated instead of worked around — an inspector who cannot sign
 * needs to know that now, standing at the tool, not at submit.
 *
 * ENTITY TYPE. 'inspection_signature', never 'inspections' — the latter is the
 * Photos grid's own query on both the detail page and the PDF, and a signature
 * filed there renders as a photo tile. See SignatureCapture's entityType prop.
 */
const INSPECTION_SIGNATURE_ENTITY_TYPE = 'inspection_signature';

function SignatureSlot({
  label,
  formName,
  submissionId,
  draftLoading,
  onSigned,
  onCleared,
}: {
  label: string;
  formName: string;
  submissionId?: string;
  draftLoading?: boolean;
  onSigned: (r2Key: string) => void;
  onCleared: () => void;
}) {
  if (submissionId) {
    return (
      <SignatureCapture
        label={label}
        formName={formName}
        submissionId={submissionId}
        entityType={INSPECTION_SIGNATURE_ENTITY_TYPE}
        onSigned={onSigned}
        onCleared={onCleared}
      />
    );
  }

  return (
    <div className="bg-fieldiq-card rounded-xl p-4">
      <h3 className="font-semibold mb-3 text-white">{label}</h3>
      {draftLoading ? (
        <div className="flex items-center gap-2 text-fieldiq-muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Preparing signature capture...
        </div>
      ) : (
        <div className="flex items-start gap-2 text-sm text-amber-300">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>
            Signature capture is not ready yet. Select the equipment above; if this persists,
            the inspection draft did not save and the signature cannot be attached to it.
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Power Tong Structured Form ─────────────────────────────────────────────

interface PowerTongFormProps {
  template: InspectionTemplate;
  equipment: any;
  user: any;
  company: any;
  equipmentId?: string;
  submissionId?: string;
  /** A saved draft being reopened. See EquipmentInspectionFormProps.existing. */
  existing?: InspectionRow;
  draftLoading?: boolean;
  /** True when the tech arrived from an equipment page rather than the dropdown. */
  lockEquipmentFields?: boolean;
  /** The job's location, when the tech arrived from a job's equipment card. */
  jobLocation?: string;
  onSaveDraft: (data: any) => void;
  onSubmit: (data: any) => void;
  isSaving: boolean;
}

function PowerTongStructuredForm({ template, equipment, user, company, equipmentId, submissionId, existing, draftLoading, lockEquipmentFields, jobLocation, onSaveDraft, onSubmit, isSaving }: PowerTongFormProps) {
  const selectedEquipment = equipment;
  const timeZone = useTenantTimeZone();

  // The asset's own identity — make, model, serial — is not the tech's to
  // retype when they navigated here from that asset's page. Locked only once
  // filled: an equipment record that carries no manufacturer leaves the field
  // editable rather than permanently blank. Job location stays editable on
  // every path; it describes where the inspection happened, which is not what
  // the equipment record's location field means.
  const lockIfFilled = (value: string) => !!lockEquipmentFields && !!value;
  const lockedInputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]';
  const openInputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]';

  const formType = (template.form_type || (template as any).formType || 'power_tong') as string;
  // `tongMake` / `tongModel` are named for the Power Tong form, which came
  // first; CRTi, elevators and slips reuse the same fields for their own tool,
  // so only Power Tong should say "Tong". See MAKE_MODEL_LABELS in
  // InspectionDetailPage.tsx and the API's inspection-templates.routes.ts —
  // entry, screen and PDF have to agree on what the field is called.
  const makeLabel = formType === 'power_tong' ? 'Tong Make' : 'Make';
  const modelLabel = formType === 'power_tong' ? 'Tong Model' : 'Model';
  const isCrti = formType === 'crti';
  const isElevators = formType === 'elevators';
  const isSlipElevatorL1 = formType === 'slip_elevator_l1';
  const isRotarySlipsL1 = formType === 'rotary_slips_l1';

  const userName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || ''
    : '';

  // Job Location records where the inspection happened. Three sources, in
  // descending order of how well they answer that:
  //   1. the job the tech opened this from — the rig/well/pad they are standing on
  //   2. the equipment record's own location — the yard, a stand-in at best
  //   3. nothing, and they type it
  // These feed useState initialisers, so the value has to be right on the first
  // render; the job location arrives in router state, which it is.
  const jobLocationSeed =
    jobLocation || selectedEquipment?.location || selectedEquipment?.job_location || '';

  // Auto-populate general info from equipment and user
  const [generalInfo, setGeneralInfo] = useState({
    // Power Tong fields
    tongMake: selectedEquipment?.make || selectedEquipment?.manufacturer || '',
    tongModel: selectedEquipment?.model || selectedEquipment?.tong_model || '',
    serialNumber: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || '',
    jobLocation: jobLocationSeed,
    operator: userName,
    date: todayCalendarDate(timeZone) ?? '',
    // CRTi-specific fields
    companyName: company?.name || company?.company_name || '',
    rigName: '',
    wellName: '',
    location: jobLocationSeed,
    time: tenantClockHHMM(timeZone),
    inspectorName: userName,
    supervisor: '',
    toolModel: selectedEquipment?.model || selectedEquipment?.tool_model || '',
    toolSerialNumber: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || '',
    jobType: '',
    pipeSize: selectedEquipment?.pipe_size || selectedEquipment?.pipeSize || '',
    pipeWeight: '',
    meterageSinceLastInspection: '',
    totalLifetimeMeterage: '',
    // SPREAD LAST, AND ONLY OVER THE KEYS THE ROW ACTUALLY CARRIES. A resumed
    // draft overrides what the equipment record seeded above, because a value a
    // person typed outranks one derived from the asset; where the row is silent
    // the derived seed survives rather than being blanked. resumeGeneralInfo
    // omits an absent key rather than returning '' for it — the difference
    // between "they cleared this" and "this was never stored".
    ...(existing ? resumeGeneralInfo(existing) : {}),
  });

  // Track pass/minor/fail + comment per item
  // The answered checklist — the bulk of what a resumed draft is FOR, and the
  // one part of the inverse that needs no per-type branch: both builders key
  // results as `${sectionNumber}_${itemIndex}` for all five form types.
  const [itemResults, setItemResults] = useState<Record<string, { result: string; comment: string }>>(
    existing ? resumeItemResults(existing) : {},
  );
  const [overallResult, setOverallResult] = useState<string>(
    String(existing?.overall_result ?? existing?.overallResult ?? ''),
  );
  const [notes, setNotes] = useState(String(existing?.notes ?? ''));
  const [signatureData, setSignatureData] = useState(
    String(existing?.inspector_signature ?? existing?.inspectorSignature ?? ''),
  );
  const [photoUrls, setPhotoUrls] = useState<{ key: string; url: string; label?: string }[]>([]);

  // Elevators-specific state
  const [elevatorInfo, setElevatorInfo] = useState({
    inspectionType: '',
    elevatorManufacturer: selectedEquipment?.manufacturer || selectedEquipment?.make || '',
    elevatorModel: selectedEquipment?.model || '',
    elevatorSize: selectedEquipment?.size || '',
    ratedCapacity: selectedEquipment?.capacity || selectedEquipment?.rated_capacity || '',
    serialNumber: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || '',
    assetNumber: '',
    rig: '',
    wellName: '',
    location: selectedEquipment?.location || selectedEquipment?.job_location || '',
    ...(existing ? resumeElevatorInfo(existing) : {}),
  });
  const [deficiencies, setDeficiencies] = useState<{ description: string; severity: 'low' | 'medium' | 'critical' }[]>(
    existing ? resumeDeficiencies(existing) : [],
  );
  const [supervisorSignature, setSupervisorSignature] = useState(
    String(existing?.supervisor_signature ?? existing?.supervisorSignature ?? ''),
  );

  // Slip Elevator L1-specific state
  const [slipElevatorInfo, setSlipElevatorInfo] = useState({
    elevatorType: '',
    manufacturer: selectedEquipment?.manufacturer || selectedEquipment?.make || '',
    model: selectedEquipment?.model || '',
    size: selectedEquipment?.size || '',
    ratedCapacity: selectedEquipment?.capacity || selectedEquipment?.rated_capacity || '',
    serialNumber: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || '',
    assetNumber: '',
    rig: '',
    wellName: '',
    location: selectedEquipment?.location || selectedEquipment?.job_location || '',
    ...(existing ? resumeSlipElevatorInfo(existing) : {}),
  });

  // Rotary Slips L1-specific state
  const [rotarySlipsInfo, setRotarySlipsInfo] = useState({
    slipType: '',
    manufacturer: selectedEquipment?.manufacturer || selectedEquipment?.make || '',
    size: selectedEquipment?.size || '',
    assetNumber: '',
    serialNumber: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || '',
    rig: '',
    ...(existing ? resumeRotarySlipsInfo(existing) : {}),
  });

  // All template sections are checklist sections (general info is hardcoded, not in template)
  const checklistSections = template.sections;
  const totalItems = checklistSections.reduce((sum, s) => sum + s.items.length, 0);
  const completedItems = Object.values(itemResults).filter(v => v.result === 'pass' || v.result === 'minor' || v.result === 'fail').length;
  const progressPct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const toggleItem = (key: string, value: 'pass' | 'minor' | 'fail') => {
    setItemResults(prev => {
      const existing = prev[key] || { result: '', comment: '' };
      return {
        ...prev,
        [key]: { ...existing, result: existing.result === value ? '' : value },
      };
    });
  };

  const setItemComment = (key: string, comment: string) => {
    setItemResults(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { result: '', comment: '' }), comment },
    }));
  };

  const getStatusIcon = (result: string) => {
    switch (result) {
      case 'pass':
        return <CheckCircle size={20} className="text-fieldiq-success" />;
      case 'minor':
        return <AlertTriangle size={20} className="text-fieldiq-accent" />;
      case 'fail':
        return <XCircle size={20} className="text-fieldiq-danger" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-fieldiq-border" />;
    }
  };

  const buildSubmissionData = (status: 'draft' | 'submitted') => {
    // Build flat results keyed by `${section.number}_${itemIndex}`
    const results: Record<string, { result: string | null; comment: string | null }> = {};
    checklistSections.forEach((section, sectionIdx) => {
      const sectionNum = section.number ?? (sectionIdx + 1);
      section.items.forEach((_item, idx) => {
        const key = `${sectionNum}_${idx}`;
        const entry = itemResults[key];
        results[key] = {
          result: entry?.result || null,
          comment: entry?.comment || null,
        };
      });
    });

    if (isElevators) {
      return {
        form_type: formType,
        equipment_id: selectedEquipment?.id || null,
        status,
        tong_model: elevatorInfo.elevatorModel || null,
        serial_number: elevatorInfo.serialNumber || null,
        job_location: elevatorInfo.location || null,
        operator_name: userName || null,
        inspection_date: generalInfo.date || null,
        elevator_manufacturer: elevatorInfo.elevatorManufacturer || null,
        elevator_model: elevatorInfo.elevatorModel || null,
        elevator_size: elevatorInfo.elevatorSize || null,
        rated_capacity: elevatorInfo.ratedCapacity || null,
        // `rig_name`, not `rig` — same fact, and rig_name is the column. The
        // handler reads either key so this is not a coordinated change, but
        // posting the dead key is what made 0305 unable to map it.
        rig_name: elevatorInfo.rig || null,
        well_name: elevatorInfo.wellName || null,
        inspection_time: generalInfo.time || null,
        results,
        deficiencies: deficiencies.filter(d => d.description.trim()),
        overall_result: overallResult || null,
        notes: notes || null,
        inspector_signature: signatureData || null,
        supervisor_signature: supervisorSignature || null,
      };
    }

    if (isSlipElevatorL1) {
      return {
        form_type: formType,
        equipment_id: selectedEquipment?.id || null,
        status,
        tong_model: slipElevatorInfo.model || null,
        serial_number: slipElevatorInfo.serialNumber || null,
        job_location: slipElevatorInfo.location || null,
        operator_name: userName || null,
        inspection_date: generalInfo.date || null,
        elevator_type: slipElevatorInfo.elevatorType || null,
        elevator_manufacturer: slipElevatorInfo.manufacturer || null,
        elevator_model: slipElevatorInfo.model || null,
        elevator_size: slipElevatorInfo.size || null,
        rated_capacity: slipElevatorInfo.ratedCapacity || null,
        rig_name: slipElevatorInfo.rig || null,
        well_name: slipElevatorInfo.wellName || null,
        inspection_time: generalInfo.time || null,
        results,
        deficiencies: deficiencies.filter(d => d.description.trim()),
        overall_result: overallResult || null,
        notes: notes || null,
        inspector_signature: signatureData || null,
      };
    }

    if (isRotarySlipsL1) {
      return {
        form_type: formType,
        equipment_id: selectedEquipment?.id || null,
        status,
        tong_model: rotarySlipsInfo.slipType || null,
        serial_number: rotarySlipsInfo.serialNumber || null,
        job_location: null,
        operator_name: userName || null,
        inspection_date: generalInfo.date || null,
        slip_type: rotarySlipsInfo.slipType || null,
        slip_manufacturer: rotarySlipsInfo.manufacturer || null,
        slip_size: rotarySlipsInfo.size || null,
        rig_name: rotarySlipsInfo.rig || null,
        inspection_time: generalInfo.time || null,
        results,
        deficiencies: deficiencies.filter(d => d.description.trim()),
        overall_result: overallResult || null,
        notes: notes || null,
        inspector_signature: signatureData || null,
      };
    }

    if (isCrti) {
      return {
        form_type: formType,
        equipment_id: selectedEquipment?.id || null,
        status,
        // Common columns (so the list + detail page render equipment / inspector)
        tong_model: generalInfo.toolModel || null,
        serial_number: generalInfo.toolSerialNumber || null,
        job_location: generalInfo.location || null,
        operator_name: generalInfo.inspectorName || null,
        inspection_date: generalInfo.date || null,
        // CRTi-specific general info.
        //
        // THREE KEYS DELIBERATELY NOT SENT — company_name, tool_model and
        // tool_serial_number. Each was posted on every submission and each was
        // discarded: POST /casing/inspection-submissions maps a fixed list of
        // body keys and none of the three is on it, because none of the three is
        // a column. 0305 added columns for the four fields below and pointedly
        // did not add them for these:
        //
        //   company_name        readOnly in this form, derived from the company
        //                       record. The PDF query already aliases
        //                       co.name AS company_name, so a column of that
        //                       name would collide with it through SELECT s.*.
        //   tool_model          duplicates tong_model,     sent above and stored.
        //   tool_serial_number  duplicates serial_number,  sent above and stored.
        //
        // Nothing is lost by dropping them; the two duplicates carry the same
        // `generalInfo` values that tong_model / serial_number already send.
        // The Company field stays on screen — it is real information, it is just
        // the tenant's own name and already on the record via company_id.
        rig_name: generalInfo.rigName || null,
        well_name: generalInfo.wellName || null,
        inspection_time: generalInfo.time || null,
        supervisor: generalInfo.supervisor || null,
        job_type: generalInfo.jobType || null,
        pipe_size: generalInfo.pipeSize || null,
        pipe_weight: generalInfo.pipeWeight || null,
        meterage_since_last_inspection: generalInfo.meterageSinceLastInspection || null,
        total_lifetime_meterage: generalInfo.totalLifetimeMeterage || null,
        results,
        overall_result: overallResult || null,
        notes: notes || null,
        inspector_signature: signatureData || null,
      };
    }

    return {
      form_type: formType,
      equipment_id: selectedEquipment?.id || null,
      status,
      tong_make: generalInfo.tongMake || null,
      tong_model: generalInfo.tongModel || null,
      serial_number: generalInfo.serialNumber || null,
      job_location: generalInfo.jobLocation || null,
      operator_name: generalInfo.operator || null,
      inspection_date: generalInfo.date || null,
      results,
      overall_result: overallResult || null,
      notes: notes || null,
      inspector_signature: signatureData || null,
    };
  };

  const canSubmit = !!overallResult && !isSaving;

  return (
    <div className="space-y-4 pb-4">
      {/* Progress bar */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">Inspection Progress</span>
          <span className="text-sm font-bold text-fieldiq-accent">{completedItems} / {totalItems}</span>
        </div>
        <div className="h-3 bg-fieldiq-dark rounded-full overflow-hidden">
          <div
            className="h-full bg-fieldiq-accent-signal rounded-full transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Section 1: General Information (auto-populated) */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 text-white text-base">
          General Information
        </h3>
        {isRotarySlipsL1 ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Slip Type</label>
            <input
              type="text"
              value={rotarySlipsInfo.slipType}
              onChange={e => setRotarySlipsInfo({ ...rotarySlipsInfo, slipType: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="e.g. Rotary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Manufacturer</label>
              <input
                type="text"
                value={rotarySlipsInfo.manufacturer}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Size</label>
              <input
                type="text"
                value={rotarySlipsInfo.size}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Asset Number</label>
              <input
                type="text"
                value={rotarySlipsInfo.assetNumber}
                onChange={e => setRotarySlipsInfo({ ...rotarySlipsInfo, assetNumber: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Asset number"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Serial Number</label>
              <input
                type="text"
                value={rotarySlipsInfo.serialNumber}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Rig</label>
            <input
              type="text"
              value={rotarySlipsInfo.rig}
              onChange={e => setRotarySlipsInfo({ ...rotarySlipsInfo, rig: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="e.g. Rig 12"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Inspector Name</label>
              <input
                type="text"
                value={generalInfo.inspectorName}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={generalInfo.date}
                onChange={v => setGeneralInfo({ ...generalInfo, date: v })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Time</label>
            <input
              type="time"
              value={generalInfo.time}
              onChange={e => setGeneralInfo({ ...generalInfo, time: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
            />
          </div>
        </div>
        ) : isSlipElevatorL1 ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Elevator Type</label>
            <input
              type="text"
              value={slipElevatorInfo.elevatorType}
              onChange={e => setSlipElevatorInfo({ ...slipElevatorInfo, elevatorType: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="e.g. Slip Type"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Manufacturer</label>
              <input
                type="text"
                value={slipElevatorInfo.manufacturer}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Model</label>
              <input
                type="text"
                value={slipElevatorInfo.model}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Size</label>
              <input
                type="text"
                value={slipElevatorInfo.size}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Rated Capacity</label>
              <input
                type="text"
                value={slipElevatorInfo.ratedCapacity}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Serial Number</label>
              <input
                type="text"
                value={slipElevatorInfo.serialNumber}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Asset Number</label>
              <input
                type="text"
                value={slipElevatorInfo.assetNumber}
                onChange={e => setSlipElevatorInfo({ ...slipElevatorInfo, assetNumber: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Asset number"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Rig</label>
              <input
                type="text"
                value={slipElevatorInfo.rig}
                onChange={e => setSlipElevatorInfo({ ...slipElevatorInfo, rig: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="e.g. Rig 12"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Well Name</label>
              <input
                type="text"
                value={slipElevatorInfo.wellName}
                onChange={e => setSlipElevatorInfo({ ...slipElevatorInfo, wellName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Well name"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Location</label>
            <input
              type="text"
              value={slipElevatorInfo.location}
              onChange={e => setSlipElevatorInfo({ ...slipElevatorInfo, location: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="From active job"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Inspector Name</label>
              <input
                type="text"
                value={generalInfo.inspectorName}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={generalInfo.date}
                onChange={v => setGeneralInfo({ ...generalInfo, date: v })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Time</label>
            <input
              type="time"
              value={generalInfo.time}
              onChange={e => setGeneralInfo({ ...generalInfo, time: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
            />
          </div>
        </div>
        ) : isElevators ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Inspection Type</label>
            <select
              value={elevatorInfo.inspectionType}
              onChange={e => setElevatorInfo({ ...elevatorInfo, inspectionType: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
            >
              <option value="">-- Select inspection type --</option>
              <option value="Daily/Pre-Use">Daily/Pre-Use</option>
              <option value="Monthly">Monthly</option>
              <option value="CAT III">CAT III</option>
              <option value="Post-Incident">Post-Incident</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Elevator Manufacturer</label>
              <input
                type="text"
                value={elevatorInfo.elevatorManufacturer}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Elevator Model</label>
              <input
                type="text"
                value={elevatorInfo.elevatorModel}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Elevator Size</label>
              <input
                type="text"
                value={elevatorInfo.elevatorSize}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Rated Capacity</label>
              <input
                type="text"
                value={elevatorInfo.ratedCapacity}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Serial Number</label>
              <input
                type="text"
                value={elevatorInfo.serialNumber}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Asset Number</label>
              <input
                type="text"
                value={elevatorInfo.assetNumber}
                onChange={e => setElevatorInfo({ ...elevatorInfo, assetNumber: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Asset number"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Rig</label>
              <input
                type="text"
                value={elevatorInfo.rig}
                onChange={e => setElevatorInfo({ ...elevatorInfo, rig: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="e.g. Rig 12"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Well Name</label>
              <input
                type="text"
                value={elevatorInfo.wellName}
                onChange={e => setElevatorInfo({ ...elevatorInfo, wellName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Well name"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Location</label>
            <input
              type="text"
              value={elevatorInfo.location}
              onChange={e => setElevatorInfo({ ...elevatorInfo, location: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="From active job"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Inspector Name</label>
              <input
                type="text"
                value={generalInfo.inspectorName}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={generalInfo.date}
                onChange={v => setGeneralInfo({ ...generalInfo, date: v })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Time</label>
            <input
              type="time"
              value={generalInfo.time}
              onChange={e => setGeneralInfo({ ...generalInfo, time: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
            />
          </div>
        </div>
        ) : isCrti ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Company</label>
              <input
                type="text"
                value={generalInfo.companyName}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Rig Name / Number</label>
              <input
                type="text"
                value={generalInfo.rigName}
                onChange={e => setGeneralInfo({ ...generalInfo, rigName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                aria-label="Rig Name / Number"
                placeholder="e.g. Rig 12"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Well Name</label>
              <input
                type="text"
                value={generalInfo.wellName}
                onChange={e => setGeneralInfo({ ...generalInfo, wellName: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                aria-label="Well Name"
                placeholder="Well name"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Location</label>
              <input
                type="text"
                value={generalInfo.location}
                onChange={e => setGeneralInfo({ ...generalInfo, location: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="From active job"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={generalInfo.date}
                onChange={v => setGeneralInfo({ ...generalInfo, date: v })}
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Time</label>
              <input
                type="time"
                value={generalInfo.time}
                onChange={e => setGeneralInfo({ ...generalInfo, time: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Inspector Name</label>
              <input
                type="text"
                value={generalInfo.inspectorName}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Supervisor</label>
              <input
                type="text"
                value={generalInfo.supervisor}
                onChange={e => setGeneralInfo({ ...generalInfo, supervisor: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="Supervisor name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Tool Model</label>
              <input
                type="text"
                value={generalInfo.toolModel}
                readOnly={lockIfFilled(generalInfo.toolModel)}
                onChange={e => setGeneralInfo({ ...generalInfo, toolModel: e.target.value })}
                className={lockIfFilled(generalInfo.toolModel) ? lockedInputClass : openInputClass}
                placeholder="Auto-populated from equipment"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Tool Serial Number</label>
              <input
                type="text"
                value={generalInfo.toolSerialNumber}
                readOnly={lockIfFilled(generalInfo.toolSerialNumber)}
                onChange={e => setGeneralInfo({ ...generalInfo, toolSerialNumber: e.target.value })}
                className={lockIfFilled(generalInfo.toolSerialNumber) ? lockedInputClass : openInputClass}
                placeholder="Auto-populated from equipment"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Job Type</label>
              <select
                value={generalInfo.jobType}
                onChange={e => setGeneralInfo({ ...generalInfo, jobType: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              >
                <option value="">-- Select job type --</option>
                <option value="surface">Surface</option>
                <option value="intermediate">Intermediate</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Pipe Size</label>
              <input
                type="text"
                value={generalInfo.pipeSize}
                onChange={e => setGeneralInfo({ ...generalInfo, pipeSize: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder='e.g. 5-1/2"'
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Pipe Weight</label>
              <input
                type="text"
                value={generalInfo.pipeWeight}
                onChange={e => setGeneralInfo({ ...generalInfo, pipeWeight: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                placeholder="e.g. 20 ppf"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Meterage Run Since Last Inspection</label>
              <input
                type="number"
                value={generalInfo.meterageSinceLastInspection}
                onChange={e => setGeneralInfo({ ...generalInfo, meterageSinceLastInspection: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                aria-label="Meterage Run Since Last Inspection"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Total Lifetime Meterage</label>
              <input
                type="number"
                value={generalInfo.totalLifetimeMeterage}
                onChange={e => setGeneralInfo({ ...generalInfo, totalLifetimeMeterage: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
                aria-label="Total Lifetime Meterage"
                placeholder="0"
              />
            </div>
          </div>
        </div>
        ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">{makeLabel}</label>
              <input
                type="text"
                value={generalInfo.tongMake}
                aria-label={makeLabel}
                readOnly={lockIfFilled(generalInfo.tongMake)}
                onChange={e => setGeneralInfo({ ...generalInfo, tongMake: e.target.value })}
                className={lockIfFilled(generalInfo.tongMake) ? lockedInputClass : openInputClass}
                placeholder="Auto-populated from equipment"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">{modelLabel}</label>
              <input
                type="text"
                value={generalInfo.tongModel}
                aria-label={modelLabel}
                readOnly={lockIfFilled(generalInfo.tongModel)}
                onChange={e => setGeneralInfo({ ...generalInfo, tongModel: e.target.value })}
                className={lockIfFilled(generalInfo.tongModel) ? lockedInputClass : openInputClass}
                placeholder="Auto-populated from equipment"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Serial Number</label>
            <input
              type="text"
              value={generalInfo.serialNumber}
              aria-label="Serial Number"
              readOnly={lockIfFilled(generalInfo.serialNumber)}
              onChange={e => setGeneralInfo({ ...generalInfo, serialNumber: e.target.value })}
              className={lockIfFilled(generalInfo.serialNumber) ? lockedInputClass : openInputClass}
              placeholder="Auto-populated from equipment"
            />
          </div>
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Job Location</label>
            <input
              type="text"
              value={generalInfo.jobLocation}
              aria-label="Job Location"
              onChange={e => setGeneralInfo({ ...generalInfo, jobLocation: e.target.value })}
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm min-h-[44px]"
              placeholder="From active job"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Operator</label>
              <input
                type="text"
                value={generalInfo.operator}
                readOnly
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-muted text-sm min-h-[44px]"
              />
            </div>
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Date</label>
              <DatePicker
                value={generalInfo.date}
                onChange={v => setGeneralInfo({ ...generalInfo, date: v })}
              />
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Sections 2–7: Checklist items with Pass/Fail */}
      {checklistSections.map((section, sectionIdx) => {
        const sectionKey = section.number ?? (sectionIdx + 1);
        return (
        <div key={sectionKey} className="bg-fieldiq-card rounded-xl p-4">
          <h3 className="font-semibold mb-3 text-white text-base">{section.title}</h3>
          <div className="space-y-2">
            {section.items.map((item, idx) => {
              const key = `${sectionKey}_${idx}`;
              const entry = itemResults[key] || { result: '', comment: '' };
              const label = typeof item === 'string' ? item : (item.label || item.id || `Item ${idx + 1}`);
              return (
                <div key={key} className="bg-fieldiq-dark rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{label}</span>
                    {getStatusIcon(entry.result)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => toggleItem(key, 'pass')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        entry.result === 'pass' ? 'bg-fieldiq-success text-white' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Pass
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleItem(key, 'minor')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        entry.result === 'minor' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Minor
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleItem(key, 'fail')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        entry.result === 'fail' ? 'bg-fieldiq-danger text-white' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Fail
                    </button>
                  </div>
                  {(entry.result === 'minor' || entry.result === 'fail') && (
                    <input
                      type="text"
                      value={entry.comment}
                      onChange={(e) => setItemComment(key, e.target.value)}
                      placeholder={entry.result === 'fail' ? 'Describe the issue...' : 'Notes (optional)...'}
                      className="w-full mt-2 bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })}

      {/* Photo reminder for Rotary Slips L1 — after Inserts / Dies section */}
      {isRotarySlipsL1 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-400 shrink-0" />
          <span className="text-sm text-amber-300">Photo of insert faces required</span>
        </div>
      )}

      {/* Deficiencies (Elevators / Slip Elevator L1 / Rotary Slips L1) */}
      {(isElevators || isSlipElevatorL1 || isRotarySlipsL1) && (
        <div className="bg-fieldiq-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-white text-base">Deficiencies</h3>
            {deficiencies.length < 10 && (
              <button
                type="button"
                onClick={() => setDeficiencies(prev => [...prev, { description: '', severity: 'low' }])}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-fieldiq-accent bg-fieldiq-accent/10 rounded-lg hover:bg-fieldiq-accent/20 transition-colors"
              >
                <Plus size={14} />
                Add Deficiency
              </button>
            )}
          </div>
          {deficiencies.length === 0 && (
            <p className="text-fieldiq-muted text-sm">No deficiencies recorded. Click "Add Deficiency" to add one.</p>
          )}
          <div className="space-y-3">
            {deficiencies.map((def, idx) => (
              <div key={idx} className="bg-fieldiq-dark rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={def.description}
                      onChange={e => {
                        const updated = [...deficiencies];
                        updated[idx] = { ...updated[idx], description: e.target.value };
                        setDeficiencies(updated);
                      }}
                      placeholder="Describe the deficiency..."
                      className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted"
                    />
                    <div className="flex gap-2">
                      {(['low', 'medium', 'critical'] as const).map(sev => {
                        const sevStyles: Record<string, string> = {
                          low: def.severity === 'low' ? 'bg-fieldiq-muted/30 text-fieldiq-text ring-1 ring-fieldiq-muted' : 'bg-fieldiq-border text-fieldiq-text',
                          medium: def.severity === 'medium' ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500' : 'bg-fieldiq-border text-fieldiq-text',
                          critical: def.severity === 'critical' ? 'bg-red-500/20 text-red-400 ring-1 ring-red-500' : 'bg-fieldiq-border text-fieldiq-text',
                        };
                        return (
                          <button
                            key={sev}
                            type="button"
                            onClick={() => {
                              const updated = [...deficiencies];
                              updated[idx] = { ...updated[idx], severity: sev };
                              setDeficiencies(updated);
                            }}
                            className={`flex-1 py-1.5 rounded text-xs font-medium transition-colors ${sevStyles[sev]}`}
                          >
                            {sev.charAt(0).toUpperCase() + sev.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDeficiencies(prev => prev.filter((_, i) => i !== idx))}
                    className="p-1.5 text-fieldiq-muted hover:text-red-400 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photos — wait for draft submission before enabling uploads */}
      {submissionId ? (
        <PhotoUploader
          entityType="inspections"
          entityId={submissionId}
          value={photoUrls}
          onChange={setPhotoUrls}
          maxPhotos={6}
        />
      ) : draftLoading ? (
        <div className="bg-fieldiq-card rounded-xl p-4 flex items-center gap-2 text-fieldiq-muted text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Preparing photo uploads...
        </div>
      ) : equipmentId ? (
        <PhotoUploader
          entityType="equipment"
          entityId={equipmentId}
          value={photoUrls}
          onChange={setPhotoUrls}
          maxPhotos={6}
        />
      ) : (
        <div className="bg-fieldiq-card rounded-xl p-4 text-fieldiq-muted text-sm">
          Select equipment above to enable photo uploads.
        </div>
      )}

      {/* Notes */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 text-white">Notes</h3>
        <textarea
          rows={3}
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Additional notes..."
          className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white placeholder-fieldiq-muted text-sm min-h-[44px]"
        />
      </div>

      {/* Signatures — see SignatureSlot. Both hold R2 keys now, not base64. */}
      <SignatureSlot
        label="Inspector Signature"
        formName={formType}
        submissionId={submissionId}
        draftLoading={draftLoading}
        onSigned={setSignatureData}
        onCleared={() => setSignatureData('')}
      />

      {/* Supervisor Signature (Elevators only) */}
      {isElevators && (
        <SignatureSlot
          label="Supervisor Signature"
          formName={formType}
          submissionId={submissionId}
          draftLoading={draftLoading}
          onSigned={setSupervisorSignature}
          onCleared={() => setSupervisorSignature('')}
        />
      )}

      {/* Overall Result */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 text-white">Overall Result</h3>
        {/* Named, because every checklist row above carries its own
            Pass / Minor / Fail buttons — so "the Pass button" is ambiguous to a
            screen reader and to a test alike. A name on the group answers both
            without anyone traversing the DOM to find it. */}
        <div className="flex gap-2" role="group" aria-label="Overall Result">
          {(['pass', 'fail', 'conditional'] as const).map(value => {
            const labels: Record<string, string> = { pass: 'Pass', fail: 'Fail', conditional: 'Conditional' };
            const activeStyles: Record<string, string> = {
              pass: 'bg-emerald-600 text-white',
              fail: 'bg-red-600 text-white',
              conditional: 'bg-amber-500 text-fieldiq-dark',
            };
            return (
              <button
                key={value}
                type="button"
                onClick={() => setOverallResult(prev => prev === value ? '' : value)}
                className={`flex-1 py-4 rounded-lg text-base font-bold transition-colors min-h-[52px] ${
                  overallResult === value
                    ? activeStyles[value]
                    : 'bg-fieldiq-border text-fieldiq-text hover:bg-fieldiq-border/80'
                }`}
              >
                {labels[value]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Auto-Fail Conditions (Elevators only) */}
      {isElevators && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertOctagon size={18} className="text-amber-400" />
            <h3 className="font-semibold text-amber-400 text-base">Auto-Fail Conditions</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-amber-200/80">
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Structural crack</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Bent load-bearing component</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Failed latch assembly</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Failed safety lock</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing hinge pin retention</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Elongated bail ear connection</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing load rating</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Expired certification</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Evidence of unauthorized weld repair</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Severe corrosion affecting structural integrity</li>
          </ul>
          <p className="mt-3 text-xs text-amber-300/70 italic">
            If any of the above are identified, equipment must be immediately removed from service.
          </p>
        </div>
      )}

      {/* Auto-Fail Conditions (Slip Elevator L1) */}
      {isSlipElevatorL1 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertOctagon size={18} className="text-amber-400" />
            <h3 className="font-semibold text-amber-400 text-base">Auto-Fail Conditions</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-amber-200/80">
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Structural crack</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Broken slip segment</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing slip segment</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing die insert</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Broken die insert affecting gripping surface</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Failed latch</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Failed safety lock</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing hinge pin retention</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Bent load-bearing component</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Illegible load rating</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Incorrect elevator size for pipe being run</li>
          </ul>
          <p className="mt-3 text-xs text-amber-300/70 italic">
            If any of the above are identified, remove elevator from service immediately.
          </p>
        </div>
      )}

      {/* Auto-Fail Conditions (Rotary Slips L1) */}
      {isRotarySlipsL1 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertOctagon size={18} className="text-amber-400" />
            <h3 className="font-semibold text-amber-400 text-base">Auto-Fail Conditions</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-amber-200/80">
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Structural crack</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing insert</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Broken segment</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Missing hinge pin</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Wrong insert size</li>
            <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Slip size does not match pipe</li>
          </ul>
          <p className="mt-3 text-xs text-amber-300/70 italic">
            If any of the above are identified, remove slips from service immediately.
          </p>
        </div>
      )}

      {/* Actions — inline at bottom of content (Pattern B) */}
      <div className="border-t border-fieldiq-border px-4 pt-4 pb-[var(--bottom-chrome)] flex gap-3 mt-4">
        <button
          type="button"
          onClick={() => onSaveDraft(buildSubmissionData('draft'))}
          disabled={isSaving}
          className="flex-1 bg-fieldiq-border text-white py-3 rounded-lg font-medium min-h-[44px] flex items-center justify-center gap-2"
        >
          {isSaving && <Loader2 size={16} className="animate-spin" />}
          Save Draft
        </button>
        <button
          type="button"
          onClick={() => onSubmit(buildSubmissionData('submitted'))}
          disabled={!canSubmit}
          className={`flex-1 py-3 rounded-lg font-medium min-h-[44px] flex items-center justify-center gap-2 transition-colors ${
            canSubmit
              ? 'bg-fieldiq-success text-white'
              : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {isSaving && <Loader2 size={16} className="animate-spin" />}
          Submit Inspection
        </button>
      </div>
    </div>
  );
}

// ─── Main Form ──────────────────────────────────────────────────────────────

interface EquipmentInspectionFormProps {
  onCancel: () => void;
  /** Equipment ID for linking uploads */
  equipmentId?: string;
  /**
   * A saved DRAFT row being reopened, already fetched.
   *
   * MUST BE RESOLVED BEFORE THIS COMPONENT MOUNTS. Nearly every field below is
   * a `useState` initialiser, so a row that arrives on a later render is never
   * read and the form silently opens blank over the top of the hand's work.
   * `InspectionResumePage` holds the spinner until the fetch settles for
   * exactly that reason — the same rule the draft-gated PhotoUploader follows.
   */
  existing?: InspectionRow;
}

export default function EquipmentInspectionForm({ onCancel, equipmentId, existing }: EquipmentInspectionFormProps) {
  useHideAgentFab();  // bottom action bar — the FAB would cover Submit.
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const timeZone = useTenantTimeZone();

  // ── Prefill handed over by an equipment detail page ──
  //
  // The equipment already knows what it is, so arriving from its page picks the
  // equipment, picks the matching inspection type, and fills General
  // Information without waiting for the dropdown's onChange. Nothing below runs
  // when the form is opened from the Safety quick actions: `prefill` is empty,
  // every `...Locked` flag is false, and the manual path is untouched.
  const prefill = (location.state ?? {}) as InspectionPrefillState;
  const prefillEquipmentId = prefill.equipmentId || null;
  // null for an equipment type this mapping does not recognise — the type
  // dropdown then stays open rather than locking in a guess.
  const prefillType = inspectionTypeForEquipmentType(prefill.equipmentType);
  const equipmentLocked = !!prefillEquipmentId;
  const typeLocked = !!prefillEquipmentId && !!prefillType;

  const prefillEquipment = useMemo(
    () => (prefillEquipmentId
      ? { id: prefillEquipmentId, type: prefill.equipmentType, ...(prefill.equipmentData || {}) }
      : null),
    [prefillEquipmentId, prefill.equipmentType, prefill.equipmentData],
  );

  // Arriving from a job's equipment card rather than from the asset's own page.
  //
  // THE JOB IS NOW PART OF THE SUBMISSION. This comment used to say the
  // opposite — "sending one would be silently dropped" — and it was true:
  // equipment_inspection_submissions had no job column, so the id arrived here
  // and was used only to seed Job Location and to label the screen. API #445
  // added `job_id`, and it is what makes an inspection visible to the rest of
  // the job's crew rather than to its author alone.
  //
  // Still NULL from three of the six doors — the Safety quick action, the More
  // menu and the driver surface all open this form with no equipment and no
  // job. That is a permanent state, not a gap: those inspections are own-only
  // and the list renders them without a job chip.
  const prefillJobId = prefill.jobId || null;
  const prefillJobName = prefill.jobName || '';
  const prefillJobLocation = prefill.jobLocation || '';

  /**
   * Stamp the job onto an outgoing payload.
   *
   * Applied at the five WRITE call sites rather than inside the six payload
   * builders, deliberately. The builders are per-form-type and one of them
   * (buildTongTruckSubmission) hardcodes `job_location: null`; adding job_id to
   * each is six chances to miss one, and a missed builder is silent — the
   * inspection saves, and simply never reaches its crew. Every write goes
   * through create() or update(), so this is the one place that cannot be
   * bypassed by adding a seventh form type.
   *
   * Omitted rather than sent as null when there is no job: the PATCH treats an
   * explicit `job_id: null` as "clear the job", which would wipe a resumed
   * draft's job on any later save from a door that did not know one.
   */
  const withJob = <T extends Record<string, any>>(data: T) =>
    (prefillJobId ? { ...data, job_id: prefillJobId } : data);

  // Load real team members for inspector dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users-for-inspection'],
    queryFn: () => api.users.list().then(r => r.data.data ?? r.data ?? []),
  });
  const users: any[] = usersData ?? [];

  // Load real equipment list
  const { data: equipmentData } = useQuery({
    queryKey: ['equipment-for-inspection'],
    queryFn: () => api.equipment.list().then(r => r.data.data ?? r.data ?? []),
  });
  const equipmentList: any[] = equipmentData ?? [];

  const userName = (u: any) =>
    `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || u.email || '';

  const currentUserName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || ''
    : '';

  // ── Reopening a saved draft ──
  //
  // A resumed row outranks the equipment-page prefill on every field it
  // carries: the prefill describes the asset, the row describes what a person
  // already typed about it. Where the row is silent the prefill still applies,
  // which is why these read as `resumed ?? prefill` and not as a replacement.
  //
  // `resumedType` is checked against the type map rather than trusted: a
  // form_type this build has no builder for would otherwise select a dropdown
  // option that does not exist, and a select with no matching option shows its
  // FIRST option while state holds the real value — a wrong form, silently.
  const resumedType = typeForFormType(existing?.form_type ?? existing?.formType);
  const resumedFormType = existing ? (existing.form_type ?? existing.formType) : null;
  const resumedTongTruckNotes = resumeTongTruckNotes(existing ?? {});

  const [formData, setFormData] = useState({
    equipment: existing?.equipment_id || existing?.equipmentId || prefillEquipmentId || '',
    inspector: existing?.operator_name || existing?.operatorName || currentUserName,
    // Widened on purpose: the dropdown's onChange writes a raw string, and the
    // literal union InspectionType would reject it.
    type: (resumedType || prefillType || TONG_TRUCK_TYPE) as string,
    date: calendarDayString(existing?.inspection_date ?? existing?.inspectionDate) || todayCalendarDate(timeZone) || '',
    time: String(existing?.inspection_time ?? existing?.inspectionTime ?? '').slice(0, 5) || tenantClockHHMM(timeZone),
    // Answers restored onto the CURRENT checklist, by position. The Tong Truck
    // rows live in code, not in a template, so this is the only shape they can
    // come back into.
    items: existing ? resumeTongTruckItems(existing, TONG_TRUCK_ITEMS()) : TONG_TRUCK_ITEMS(),
    issuesFound: resumedTongTruckNotes.issuesFound,
    overallStatus: existing ? resumeTongTruckOverall(existing) : '',
    correctiveActions: resumedTongTruckNotes.correctiveActions,
    signatureData: existing?.inspector_signature || existing?.inspectorSignature || '',
    photoUrls: [] as { key: string; url: string; label?: string }[],
  });

  const isStructured = isStructuredType(formData.type);
  const formTypeKey = isStructured ? FORM_TYPE_MAP[formData.type as StructuredType] : null;

  // The resumed row is handed to the structured child ONLY when it is the shape
  // that child renders. A tong_truck draft reaches the local checklist instead,
  // and giving its columns to a structured builder would seed one form's
  // answers into another form's boxes — which looks like data, not like a bug.
  const resumedStructuredRow =
    existing && resumedFormType === formTypeKey ? existing : undefined;

  // Fetch template when a structured type is selected
  const { data: templateData, isLoading: templateLoading } = useQuery({
    queryKey: ['inspection-template', formTypeKey],
    queryFn: () => api.casingInspections.getTemplate(formTypeKey!).then(r => r.data.data ?? r.data),
    enabled: !!formTypeKey,
    staleTime: 5 * 60 * 1000,
  });

  // Selected equipment record for auto-populating structured form fields
  const selectedEquipment = useMemo(() => {
    if (!formData.equipment) return null;
    const fromList = equipmentList.find(
      (eq: any) => eq.id === formData.equipment
    ) || null;
    // The prefill record stands in until the equipment list query resolves, and
    // stands in permanently on the field shell, whose /employee/my-equipment
    // list is scoped to the crew's assignments. Without it the structured form
    // mounts against a null equipment and its General Information seeds blank —
    // those are useState initialisers, so they never re-read the prop.
    if (prefillEquipment && prefillEquipment.id === formData.equipment) {
      return fromList ? { ...prefillEquipment, ...fromList } : prefillEquipment;
    }
    return fromList;
  }, [formData.equipment, equipmentList, prefillEquipment]);

  // Resolved equipment UUID for photo uploads — prefer explicit prop, fall back to dropdown selection
  const resolvedEquipmentId = equipmentId || selectedEquipment?.id;

  // Mutations for structured form save/submit
  // Adopting the resumed row's id is what makes this a RESUME rather than a
  // copy: every save from here PATCHes that row. It also disarms the
  // auto-create effect below, whose first guard returns once draftId is set —
  // without it, reopening a draft would mint a second one beside it every time.
  const [draftId, setDraftId] = useState<string | null>(existing?.id ?? null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const draftCreatingRef = useRef(false);

  // The form_type the draft row should carry, and what has to exist before it
  // can be created. A structured type waits for its template; Tong Truck has no
  // template to wait for, so it waits for an equipment selection instead —
  // that's the same signal its PhotoUploader already gates on, and it keeps
  // merely opening the form from leaving an empty draft behind.
  const draftFormType = isStructured ? formTypeKey : TONG_TRUCK_FORM_TYPE;
  const draftReady = isStructured ? !!templateData : !!resolvedEquipmentId;

  // A draft row is created for one form_type. Crossing between the Tong Truck
  // checklist and a structured template changes the shape the row has to be, so
  // drop the reference and let the effect below mint a fresh one. The abandoned
  // draft is invisible — SafetyRecordsScreen lists status='submitted' only.
  const wasStructuredRef = useRef(isStructured);
  useEffect(() => {
    if (wasStructuredRef.current === isStructured) return;
    wasStructuredRef.current = isStructured;
    setDraftId(null);
  }, [isStructured]);

  // Auto-create a draft submission as soon as the selected type can have one,
  // so PhotoUploader has a real submission UUID from the start. Photos upload
  // against that UUID under entityType 'inspections', which is what
  // InspectionDetailPage reads back — see the PhotoUploader blocks below.
  useEffect(() => {
    if (!draftFormType || !draftReady || draftId || draftCreatingRef.current) return;
    draftCreatingRef.current = true;
    setDraftLoading(true);

    // `prefillJobId` inline rather than withJob(): the helper is rebuilt every
    // render, so naming it here would either re-run this effect continuously or
    // require silencing exhaustive-deps. The id is a stable primitive and is
    // the only thing the helper contributes.
    api.casingInspections.create({
      form_type: draftFormType,
      status: 'draft',
      equipment_id: resolvedEquipmentId || undefined,
      ...(prefillJobId ? { job_id: prefillJobId } : {}),
    }).then(res => {
      const id = res?.data?.data?.id || res?.data?.id;
      if (id) setDraftId(id);
    }).catch(err => {
      console.error('Failed to create draft inspection submission:', err);
      // Non-fatal: form still works, photos fall back to equipment-linked uploads
    }).finally(() => {
      draftCreatingRef.current = false;
      setDraftLoading(false);
    });
    // `resolvedEquipmentId` is read in the body, so it belongs here. Adding it
    // does not mint extra drafts: once `draftId` is set the guard above returns
    // first, so a re-run from switching equipment is a no-op. Before a draft
    // exists it is the right behaviour — the draft is created against whichever
    // equipment is selected at the time.
  }, [draftFormType, draftReady, draftId, resolvedEquipmentId, prefillJobId]);

  const handleStructuredSaveDraft = async (data: any) => {
    setIsSaving(true);
    try {
      if (draftId) {
        await api.casingInspections.update(draftId, withJob(data));
      } else {
        const res = await api.casingInspections.create(withJob(data));
        const id = res?.data?.data?.id || res?.data?.id;
        if (id) setDraftId(id);
      }
      queryClient.invalidateQueries({ queryKey: ['casing-inspection-submissions'] });
      toast.success('Draft saved');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStructuredSubmit = async (data: any) => {
    setIsSaving(true);
    try {
      let id = draftId;
      if (!id) {
        const res = await api.casingInspections.create(withJob({ ...data, status: 'draft' }));
        id = res?.data?.data?.id || res?.data?.id;
      }
      let disagreement: string | null = null;
      if (id) {
        // Carried on the SUBMIT as well as the create. The draft that reaches
        // here may have been minted by the auto-create effect above before the
        // job was known, and a submit that omits it would leave a finished
        // inspection invisible to the crew it belongs to.
        const submitted = await api.casingInspections.update(id, withJob({ ...data, status: 'submitted' }));
        // The API's verdict on the submitting write, never the draft create's.
        disagreement = inspectionDisagreementNotice(submitted?.data);
      }
      queryClient.invalidateQueries({ queryKey: ['casing-inspection-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['safety-records-inspections'] });
      // A submitted inspection re-checks the asset's maintenance flags (api#944).
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_FLAGS_ROOT });
      toast.success('Inspection submitted');
      // Non-blocking: the record saved as signed. The Toaster is mounted at
      // the app root, so this outlives the navigate below. Longer than the
      // default because a hand needs time to read a two-clause sentence.
      if (disagreement) toast.warning(disagreement, { duration: 12000 });
      // THE DESTINATION DEPENDS ON WHICH ROUTE TREE THE USER IS IN.
      // This was `navigate('/safety/records?tab=inspections')`, a path
      // registered only in the office tree. A casing hand got the toast above
      // and was then bounced to /field by that tree's catch-all — no record, no
      // error, and the inspection had saved correctly every single time.
      // See lib/inspection-surface.ts.
      navigate(inspectionsListPath(location.pathname));
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to submit inspection');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Tong Truck (checklist, no template) handlers ──

  // Shapes the local checklist into the payload the casing endpoint already
  // accepts from the structured types. Results are keyed `${section}_${index}`
  // the same way, so InspectionDetailPage renders them without a template.
  //
  // Every key below is a real column on equipment_inspection_submissions, and
  // the structured builders above now hold to the same rule. They used to also
  // send inspection_type, inspector_name, asset_number and photos, none of
  // which are columns — Drizzle dropped them without complaint, so they read as
  // stored data and were not. Photos are not lost by their absence: they upload
  // separately and the detail page reads them from the uploads endpoint, not
  // from the submission row.
  const buildTongTruckSubmission = (status: 'draft' | 'submitted') => {
    const results: Record<string, { result: string | null; comment: string | null; label: string }> = {};
    formData.items.forEach((item, idx) => {
      results[`1_${idx}`] = {
        result: item.status || null,
        comment: item.notes || null,
        label: item.name,
      };
    });

    // `notes` carries only what the inspector actually wrote. The third entry
    // used to be `Next inspection due: <date>` — a UI value smuggled into free
    // text, where nothing could query it and the PDF printed it back as prose.
    const notes = [
      formData.issuesFound.trim() ? `Issues found: ${formData.issuesFound.trim()}` : '',
      formData.correctiveActions.trim() ? `Corrective actions: ${formData.correctiveActions.trim()}` : '',
    ].filter(Boolean).join('\n\n');

    return {
      form_type: TONG_TRUCK_FORM_TYPE,
      equipment_id: resolvedEquipmentId || null,
      status,
      tong_make: selectedEquipment?.manufacturer || null,
      tong_model: selectedEquipment?.model || null,
      serial_number: selectedEquipment?.serial_number || selectedEquipment?.serialNumber || null,
      job_location: null,
      operator_name: formData.inspector || null,
      inspection_date: formData.date || null,
      inspection_time: formData.time || null,
      results,
      // The checklist's middle radio is "PASS with minor issues noted" and sets
      // 'minor', but overall_result carries a CHECK constraint allowing only
      // pass / fail / conditional — send the structured types' spelling.
      // Item-level results keep 'minor': they live in the results JSON, which
      // has no constraint, and InspectionDetailPage styles that value.
      overall_result: formData.overallStatus === 'minor' ? 'conditional' : (formData.overallStatus || null),
      notes: notes || null,
      inspector_signature: formData.signatureData || null,
    };
  };

  // Set once the user has tried to submit — only then is it fair to mark the
  // blank rows red. Highlighting all 47 on arrival would just be noise.
  const [showMissing, setShowMissing] = useState(false);

  const handleTongTruckSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    // The submit button used to carry `disabled` for exactly these conditions,
    // so on an incomplete form a tap did nothing at all: no toast, no scroll,
    // no indication of which of 47 checklist rows was still blank. Submitting
    // an inspection with unmarked items is still refused — but the refusal now
    // says so and puts the first offending row on screen.
    if (missingRequirements.length > 0) {
      setShowMissing(true);
      toast.error(`Cannot submit yet — ${missingRequirements.join('; ')}.`);
      const target = uncheckedItems[0]
        ? `tong-item-${uncheckedItems[0].id}`
        : !formData.overallStatus
          ? 'tong-overall-status'
          : 'tong-signature';
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setShowMissing(false);
    handleStructuredSubmit(buildTongTruckSubmission('submitted'));
  };

  const setItemStatus = (id: number, status: string) => {
    setFormData({
      ...formData,
      items: formData.items.map((item) => (item.id === id ? { ...item, status } : item)),
    });
  };

  const setItemNotes = (id: number, notes: string) => {
    setFormData({
      ...formData,
      items: formData.items.map((item) => (item.id === id ? { ...item, notes } : item)),
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle size={20} className="text-fieldiq-success" />;
      case 'minor':
        return <AlertTriangle size={20} className="text-fieldiq-accent" />;
      case 'fail':
        return <XCircle size={20} className="text-fieldiq-danger" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-fieldiq-border" />;
    }
  };

  const uncheckedItems = formData.items.filter((item) => item.status === '');

  // Everything standing between the user and a submitted inspection, in words
  // they can act on. Order matches the order the fields appear on the page.
  const missingRequirements = [
    uncheckedItems.length > 0
      ? `${uncheckedItems.length} checklist item${uncheckedItems.length === 1 ? '' : 's'} not marked`
      : '',
    !formData.overallStatus ? 'overall status not selected' : '',
    !formData.signatureData ? 'inspector signature not saved' : '',
  ].filter(Boolean);

  return (
    <form onSubmit={handleTongTruckSubmit} className="space-y-4 pb-4">
      {/* Header Info */}
      <div className="bg-fieldiq-card rounded-xl p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2 text-white">
          <Wrench size={18} />
          Equipment Inspection
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Equipment</label>
            {equipmentLocked ? (
              <input
                type="text"
                value={equipmentLabel(selectedEquipment) || equipmentLabel(prefillEquipment)}
                readOnly
                aria-label="Equipment"
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-fieldiq-muted min-h-[44px]"
              />
            ) : (
              <select
                value={formData.equipment}
                aria-label="Equipment"
                onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white min-h-[44px]"
              >
                <option value="">-- Select equipment --</option>
                {equipmentList.map((eq: any) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.asset_number || eq.assetNumber ? `${eq.asset_number || eq.assetNumber} - ` : ''}{eq.name || eq.equipment_type || 'Equipment'}
                  </option>
                ))}
              </select>
            )}
            {prefillJobId && (
              <p className="text-xs text-fieldiq-muted mt-1">
                {prefillJobName ? `For job ${prefillJobName}` : 'For the assigned job'}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-fieldiq-text mb-1">Type</label>
              {typeLocked ? (
                <>
                  <input
                    type="text"
                    value={INSPECTION_TYPE_LABELS[prefillType!]}
                    readOnly
                    aria-label="Type"
                    className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-fieldiq-muted min-h-[44px]"
                  />
                  <p className="text-xs text-fieldiq-muted mt-1">
                    Set from the equipment type{prefill.equipmentType ? ` (${prefill.equipmentType})` : ''}
                  </p>
                </>
              ) : (
                <select
                  value={formData.type}
                  aria-label="Type"
                  onChange={(e) => {
                    const newType = e.target.value;
                    if (isStructuredType(newType)) {
                      setFormData({ ...formData, type: newType });
                    } else {
                      setFormData({ ...formData, type: newType, items: TONG_TRUCK_ITEMS() });
                    }
                  }}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white min-h-[44px]"
                >
                  {INSPECTION_TYPES.map(t => (
                    <option key={t} value={t}>{INSPECTION_TYPE_LABELS[t]}</option>
                  ))}
                </select>
              )}
            </div>
            {!isStructured && (
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Inspector</label>
                <select
                  value={formData.inspector}
                  onChange={(e) => setFormData({ ...formData, inspector: e.target.value })}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white min-h-[44px]"
                >
                  <option value="">-- Select inspector --</option>
                  {users.filter((u: any) => u.role !== 'super_admin').map((u: any) => (
                    <option key={u.id} value={userName(u)}>{userName(u)}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {!isStructured && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Date</label>
                <DatePicker
                  value={formData.date}
                  onChange={(v) => setFormData({ ...formData, date: v })}
                />
              </div>
              <div>
                <label className="block text-sm text-fieldiq-text mb-1">Time</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white min-h-[44px]"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Structured form (Power Tong, future: CRTi, Elevators, Slips) ── */}
      {isStructured && (
        templateLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
            <span className="ml-2 text-fieldiq-muted text-sm">Loading inspection template...</span>
          </div>
        ) : templateData ? (
          <PowerTongStructuredForm
            template={templateData}
            equipment={selectedEquipment}
            user={user}
            company={company}
            equipmentId={resolvedEquipmentId}
            submissionId={draftId || undefined}
            existing={resumedStructuredRow}
            draftLoading={draftLoading}
            lockEquipmentFields={equipmentLocked}
            jobLocation={prefillJobLocation}
            onSaveDraft={handleStructuredSaveDraft}
            onSubmit={handleStructuredSubmit}
            isSaving={isSaving}
          />
        ) : (
          <div className="bg-fieldiq-card rounded-xl p-6 text-center">
            <p className="text-fieldiq-muted text-sm">
              Template not available. Please check your connection and try again.
            </p>
          </div>
        )
      )}

      {/* ── Generic checklist (existing behaviour) ── */}
      {!isStructured && (
        <>
          {/* Checklist Items */}
          <div className="bg-fieldiq-card rounded-xl p-4">
            <h3 className="font-semibold mb-3 text-white">Checklist Items</h3>
            <div className="space-y-3">
              {formData.items.map((item) => (
                <div
                  key={item.id}
                  id={`tong-item-${item.id}`}
                  /* Plain `ring-fieldiq-danger`, not `ring-fieldiq-danger/60`.
                     This started as a workaround: fieldiq.* tokens were bare
                     `var(...)`, so Tailwind dropped every opacity variant and
                     the ring came out the default blue. tailwind.config.js now
                     builds those variants via color-mix, so `/60` would work —
                     the full-opacity ring is kept because a missing-field
                     marker should be at full strength, not because it has to
                     be. Same for the banner below. */
                  className={`bg-fieldiq-dark rounded-lg p-3 ${
                    showMissing && item.status === '' ? 'ring-1 ring-fieldiq-danger' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-white">{item.name}</span>
                    {getStatusIcon(item.status)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setItemStatus(item.id, 'pass')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        item.status === 'pass' ? 'bg-fieldiq-success text-white' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Pass
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemStatus(item.id, 'minor')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        item.status === 'minor' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Minor
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemStatus(item.id, 'fail')}
                      className={`flex-1 py-2 rounded text-sm font-medium transition-colors ${
                        item.status === 'fail' ? 'bg-fieldiq-danger text-white' : 'bg-fieldiq-border text-fieldiq-text'
                      }`}
                    >
                      Fail
                    </button>
                  </div>
                  {(item.status === 'minor' || item.status === 'fail') && (
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => setItemNotes(item.id, e.target.value)}
                      placeholder={item.status === 'fail' ? 'Describe the issue...' : 'Notes (optional)...'}
                      className="w-full mt-2 bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder-fieldiq-muted"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Issues Found */}
          <div className="bg-fieldiq-card rounded-xl p-4">
            <h3 className="font-semibold mb-3 text-white">Issues Found</h3>
            <textarea
              rows={3}
              value={formData.issuesFound}
              onChange={(e) => setFormData({ ...formData, issuesFound: e.target.value })}
              placeholder="Describe any issues found..."
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white placeholder-fieldiq-muted"
            />
          </div>

          {/* Photos — same ladder as the structured form: attach to the draft
              submission when there is one, because InspectionDetailPage reads
              them back with listForEntity('inspections', submissionId). The
              equipment fallback below only runs if the draft failed to create,
              where photos upload but stay off the inspection, as before. */}
          {draftId ? (
            <PhotoUploader
              entityType="inspections"
              entityId={draftId}
              value={formData.photoUrls}
              onChange={(photos) => setFormData({ ...formData, photoUrls: photos })}
              maxPhotos={6}
            />
          ) : draftLoading ? (
            <div className="bg-fieldiq-card rounded-xl p-4 flex items-center gap-2 text-fieldiq-muted text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Preparing photo uploads...
            </div>
          ) : resolvedEquipmentId ? (
            <PhotoUploader
              entityType="equipment"
              entityId={resolvedEquipmentId}
              value={formData.photoUrls}
              onChange={(photos) => setFormData({ ...formData, photoUrls: photos })}
              maxPhotos={6}
            />
          ) : (
            <div className="bg-fieldiq-card rounded-xl p-4 text-fieldiq-muted text-sm">
              Select equipment above to enable photo uploads.
            </div>
          )}

          {/* Overall Status */}
          <div id="tong-overall-status" className="bg-fieldiq-card rounded-xl p-4">
            <h3 className="font-semibold mb-3 text-white">Overall Status</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 bg-fieldiq-dark rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="overallStatus"
                  checked={formData.overallStatus === 'pass'}
                  onChange={() => setFormData({ ...formData, overallStatus: 'pass' })}
                  className="w-5 h-5"
                />
                <span className="text-fieldiq-success font-medium">PASS - Ready for use</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-fieldiq-dark rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="overallStatus"
                  checked={formData.overallStatus === 'minor'}
                  onChange={() => setFormData({ ...formData, overallStatus: 'minor' })}
                  className="w-5 h-5"
                />
                <span className="text-fieldiq-accent font-medium">PASS with minor issues noted</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-fieldiq-dark rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="overallStatus"
                  checked={formData.overallStatus === 'fail'}
                  onChange={() => setFormData({ ...formData, overallStatus: 'fail' })}
                  className="w-5 h-5"
                />
                <span className="text-fieldiq-danger font-medium">FAIL - Do not use</span>
              </label>
            </div>
          </div>

          {/* Corrective Actions */}
          {formData.overallStatus !== 'pass' && formData.overallStatus !== '' && (
            <div className="bg-fieldiq-card rounded-xl p-4">
              <h3 className="font-semibold mb-3 text-white">Corrective Actions</h3>
              <textarea
                rows={3}
                value={formData.correctiveActions}
                onChange={(e) => setFormData({ ...formData, correctiveActions: e.target.value })}
                placeholder="Required fixes..."
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white placeholder-fieldiq-muted"
              />
            </div>
          )}

          {/* NO "Next Inspection Due" HERE.
              This is a per-shift pre-trip: the next one is the next shift, so a
              date answers a question nobody asked. What was here was a free
              DatePicker opening on today, whose value was concatenated into
              `notes` as "Next inspection due: <date>" — unqueryable text that
              could not drive a reminder, and that read as the inspection's own
              date because today's cell is the easy tap.

              FieldIQ already tracks inspection intervals as CAODC working days
              on the equipment record (cert_day_limit vs
              working_days_since_inspection, incremented from
              ticket_equipment_usage at office approval). A calendar date here
              would have been a second, competing interval system.

              Annual CVIP is a separate certificate and is not this form. */}

          {/* Signature — an R2 key, same as the structured forms.
              CONVERTED WITH THEM ON PURPOSE. The API's tail migration moves 27
              rows of inline base64 to R2 keys; a form still posting base64
              after it runs would keep feeding the tail it just cleared, and
              inspector_signature would carry two storage shapes indefinitely. */}
          <div id="tong-signature">
            <SignatureSlot
              label="Inspector Signature"
              formName={TONG_TRUCK_FORM_TYPE}
              submissionId={draftId || undefined}
              draftLoading={draftLoading}
              onSigned={(key) => setFormData(prev => ({ ...prev, signatureData: key }))}
              onCleared={() => setFormData(prev => ({ ...prev, signatureData: '' }))}
            />
          </div>

          {/* What is still outstanding, once the user has asked to submit. */}
          {showMissing && missingRequirements.length > 0 && (
            <div className="bg-red-900/20 border border-fieldiq-danger rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-fieldiq-danger" />
                <h3 className="font-semibold text-fieldiq-danger text-sm">Not ready to submit</h3>
              </div>
              <ul className="space-y-1 text-sm text-fieldiq-text">
                {missingRequirements.map((req) => (
                  <li key={req} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-fieldiq-danger shrink-0" />
                    <span className="first-letter:uppercase">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-fieldiq-border px-4 pt-4 pb-[var(--bottom-chrome)] flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => handleStructuredSaveDraft(buildTongTruckSubmission('draft'))}
              disabled={isSaving}
              className="flex-1 bg-fieldiq-border text-white py-3 rounded-lg font-medium min-h-[44px]"
            >
              Save Draft
            </button>
            {/* Deliberately not `disabled` when incomplete: the tap is how the
                user finds out what is missing. See handleTongTruckSubmit. */}
            <button
              type="submit"
              disabled={isSaving}
              aria-describedby={missingRequirements.length > 0 ? 'tong-submit-blocked' : undefined}
              className={`flex-1 py-3 rounded-lg font-medium transition-colors min-h-[44px] ${
                missingRequirements.length === 0 && !isSaving
                  ? 'bg-fieldiq-success text-white'
                  : 'bg-fieldiq-border text-fieldiq-muted'
              }`}
            >
              {isSaving ? 'Submitting...' : 'Complete Inspection'}
            </button>
          </div>
          {missingRequirements.length > 0 && (
            <p id="tong-submit-blocked" className="sr-only">
              {missingRequirements.join('; ')}
            </p>
          )}
        </>
      )}
    </form>
  );
}
